<template>
  <div>
    <v-toolbar
      data-js="get-height"
      class="toolbar--white action-bar overflow--y get-height"
      :class="{ 'action-bar--ml': !mainMenuIsOpen }"
      flat
    >
      <Breadcrumb :routes="breadcrumbItems" />

      <transition name="fade">
        <v-layout align-center row>
          <v-spacer />
          <v-menu
            v-if="claims.report['ROLE_POST_REPORT_TAXIJOBS_SCHEDULE'] && reportType === 'rides'"
            offset-y
            left
            fixed
          >
            <a
              class="action-bar__link action-bar__link--icon"
              :class="{ 'mr-2': reportType !== 'rides' }"
              slot="activator"
              @click.prevent
            >
              {{ $t(`${namespace}.consolidatedReport`) }}
              <v-icon color="accent">keyboard_arrow_down</v-icon>
            </a>

            <v-list class="dropdown-menu-actions__list">
              <v-list-tile
                @click="goToNewConsolidatedReport"
              >{{ $t(`${namespace}.exportConsolidatedReport`) }}</v-list-tile>

              <v-list-tile
                v-if="claims.report['ROLE_GET_REPORT_TAXIJOBS_ACTIVES']"
                @click="goToConsolidatedReports"
              >{{ $t(`${namespace}.goToConsolidatedReports`) }}</v-list-tile>
            </v-list>
          </v-menu>

          <v-tooltip
            v-if="claims.report['ROLE_GET_REPORT_RECEIPT'] && reportType === 'rides' && reportItems > 0"
            :disabled="hasSelectedCompany"
            slot="activator"
            top
          >
            <a class="action-bar__link mr-2" slot="activator" @click.prevent="handleReceiptsExport">
              {{
              isReportFiltered
              ? $t(`${namespace}.viewTheseReceipts`)
              : $t(`${namespace}.viewReceipts`)
              }}
            </a>
            <span>{{ $t('Common.selectCompany') }}</span>
          </v-tooltip>

          <v-tooltip
            v-if="
              (
                claims.report['ROLE_GET_REPORTS_ID'] ||
                claims.report['ROLE_GET_REPORT_COSTCENTER_EXPORT_VALIDATE'] ||
                claims.report['ROLE_GET_REPORT_COSTCENTER_BUDGET_EXPORT'] ||
                claims.report['ROLE_GET_REPORT_EMPLOYEE_EXPORT_VALIDATE'] ||
                claims.report['ROLE_GET_REPORT_TAXIJOBS_EXPORT_VALIDATE']
              ) &&
              reportItems > 0
            "
            :disabled="hasSelectedCompany"
            slot="activator"
            top
          >
            <v-btn
              :class="{ 'btn--circle-pulse': isReportFiltered }"
              :disabled="!hasSelectedCompany || isExporting"
              :loading="isExporting"
              color="accent"
              slot="activator"
              depressed
              round
              @click="handleReportExport"
            >
              {{
              isReportFiltered
              ? $t(`${namespace}.reportExportFiltered`)
              : $t(`${namespace}.reportExportCurrentMonth`)
              }}
            </v-btn>
            <span>{{ $t('Common.selectCompany') }}</span>
          </v-tooltip>
        </v-layout>
      </transition>
    </v-toolbar>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { mapParams } from 'utils/functions'
import Breadcrumb from 'components/Breadcrumb'

export default {
  name: 'ActionBar',
  props: {
    filters: {
      type: Object
    },
    activeTab: {
      type: Number,
      default: 0
    },
    selectedPeriod: {
      type: String
    },
    reportItems: {
      type: Number
    }
  },
  components: {
    Breadcrumb
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      hasSelectedCompany: ({ company }) => Boolean(company.selectedCompany),
      mainMenuIsOpen: ({ mainMenu }) => mainMenu.isOpen,
      claims: ({ userClaims }) => userClaims.claims
    }),

    reportType () {
      const types = {
        0: 'rides',
        1: 'costCenter',
        2: 'employee'
      }

      return types[this.activeTab] || 'rides'
    },

    isReportFiltered () {
      let filters = this.filters[this.reportType]

      for (let key in filters) {
        if (filters[key] === null) delete filters[key]
      }

      const objectKeys = filters ? Object.keys(filters).length : 0

      if (
        !filters ||
        (!filters.startDate && !filters.endDate && objectKeys === 0) ||
        (this.selectedPeriod === 'currentMonth' && objectKeys === 2) ||
        (!this.selectedPeriod && objectKeys === 2)
      ) {
        return false
      }

      return true
    }
  },
  data () {
    return {
      namespace: 'Views.ReportsPage',
      breadcrumbItems: [{ name: 'Reports', title: this.$t('Common.reports') }],
      isExporting: false,
      downloadCsvAutoRequest: null,
      downloadCsvPercentage: 0
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    createHiddenElement (url, target = '_blank') {
      let hiddenElement = document.createElement('a')
      hiddenElement.href = url
      hiddenElement.target = target
      hiddenElement.click()
    },

    goToNewConsolidatedReport () {
      this.$emit('goToNewConsolidatedReport')
    },

    goToConsolidatedReports () {
      this.$emit('goToConsolidatedReports')
    },

    handleReceiptsExport () {
      if (this.hasSelectedCompany) {
        const companyId = this.selectedCompany ? this.selectedCompany.id : null
        const filters = {
          ...this.filters['rides'],
          companyId
        }

        const query = Object.keys(filters)
          .filter(
            k =>
              filters[k] !== null &&
              filters[k] !== undefined &&
              filters[k] !== ''
          )
          .map(
            k => `${encodeURIComponent(k)}=${encodeURIComponent(filters[k])}`
          )
          .join('&')

        const uri = `${location.origin}/#/recibo-de-corridas?${query}&downloadType=csv`
        this.createHiddenElement(uri)
      }
    },

    async downloadReportCsv (id) {
      this.isExporting = true

      try {
        const { data: res } = await this.$apiService.downloadReportCsv(id)
        const { status, percentage, url } = res.data.reportBody
        this.downloadCsvPercentage = percentage

        if (status === 'COMPLETED') {
          this.createHiddenElement(url, '_self')
          clearInterval(this.downloadCsvAutoRequest)
          this.downloadCsvPercentage = 0
          this.isExporting = false
        }
      } catch (err) {
        console.log('Error', err)
        this.isExporting = false
        clearInterval(this.downloadCsvAutoRequest)
        this.showAlert({
          type: 'error',
          message: this.$t('Common.errorExport')
        })
      }
    },

    async exportReportRides (filters) {
      this.isExporting = true
      try {
        const { data: res } = await this.$apiService.exportReportRides(
          ...mapParams(filters)
        )

        if (res.data) {
          this.downloadCsvAutoRequest = setInterval(() => {
            this.downloadReportCsv(res.data)
          }, 2000)
        }
      } catch (err) {
        this.isExporting = false
        console.log('Error', err)
        clearInterval(this.downloadCsvAutoRequest)
        this.showAlert({
          type: 'error',
          message: this.$t('Common.errorExport')
        })
      }
    },

    async exportReportCostCenters (filters) {
      this.isExporting = true
      try {
        const { data: res } = await this.$apiService.exportReportCostCenters(
          ...mapParams(filters)
        )

        if (res.data) {
          this.downloadCsvAutoRequest = setInterval(() => {
            this.downloadReportCsv(res.data)
          }, 2000)
        }
      } catch (err) {
        this.isExporting = false
        console.log('Error', err)
        clearInterval(this.downloadCsvAutoRequest)
        this.showAlert({
          type: 'error',
          message: this.$t('Common.errorExport')
        })
      }
    },

    async exportReportEmployee (filters) {
      this.isExporting = true
      try {
        const { data: res } = await this.$apiService.exportReportEmployee(
          ...mapParams(filters)
        )

        if (res.data) {
          this.downloadCsvAutoRequest = setInterval(() => {
            this.downloadReportCsv(res.data)
          }, 2000)
        }
      } catch (err) {
        this.isExporting = false
        console.log('Error', err)
        clearInterval(this.downloadCsvAutoRequest)
        this.showAlert({
          type: 'error',
          message: this.$t('Common.errorExport')
        })
      }
    },

    handleReportExport () {
      if (this.hasSelectedCompany) {
        const companyId = this.selectedCompany ? this.selectedCompany.id : null
        const filters = {
          ...this.filters[this.reportType],
          companyId
        }

        if (this.reportType === 'rides') {
          this.exportReportRides(filters)
          return
        }

        if (this.reportType === 'costCenter') {
          this.exportReportCostCenters(filters)
          return
        }

        if (this.reportType === 'employee') {
          this.exportReportEmployee(filters)
        }
      }
    }
  }
}
</script>

<style lang="scss">
.btn--circle-pulse {
  position: relative;

  &:after {
    animation: pulse 3s 1s;
    background-color: #2dcc70;
    box-shadow: 0 0 0 rgba(#2dcc70, 0.8);
    content: '';
    height: 8px;
    position: absolute;
    right: 0;
    top: 0;
    width: 8px;
    z-index: 1;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(#2dcc70, 0.8);
    }
    20% {
      box-shadow: 0 0 0 15px rgba(#2dcc70, 0);
    }
    40% {
      box-shadow: 0 0 0 0 rgba(#2dcc70, 0);
    }
    60% {
      box-shadow: 0 0 0 0 rgba(#2dcc70, 0.8);
    }
    80% {
      box-shadow: 0 0 0 15px rgba(#2dcc70, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(#2dcc70, 0);
    }
  }
}
</style>
